// useForm.ts
import React, { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  CategoryInterface,
  OptionItemInterface,
  Relation,
  RelationPayload,
  SaleItemInterface,
  StoreInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useFiles } from '../../../../../../common/hooks/useFiles'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { itemValidations, minUploadFilesCount } from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { SaleItemFormView, SystemType } from '../../../../../../common/types/enums'
import { unitTypeToShort } from '../../../../../../common/utils/functions/enumToText'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'

const validations: ValidationRulesInterface[] = itemValidations

export const useForm = (
  data: SaleItemInterface,
  setData: React.Dispatch<SetStateAction<SaleItemInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: SaleItemInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const [disabled, setDisabled] = useState<boolean>(true)
  const [view, setView] = useState<SaleItemFormView>(SaleItemFormView.FORM)
  const [categoryList, setCategoryList] = useState<CategoryInterface[]>([])
  const [saleItemList, setSaleItemList] = useState<SaleItemInterface[]>([])
  const { files, setFiles, pairs, setPairs, dataInit, fetchFiles } = useFiles()
  const [attrsDisabled, setAttrsDisabled] = useState<boolean>(true)
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const { inputHandler } = useInput(setData)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()
  const [storeList, setStoreList] = useState<StoreInterface[]>([])

  const getStoreList = useCallback(async () => {
    try {
      const result = await salesApi.getStoreList()
      if (result) setStoreList(result?.data || [])
    } catch (error) {
      console.error('Error loading stores:', error)
    }
  }, [salesApi])

  useEffect(() => {
    if (el && el.files && el.files?.length > 0) {
      fetchFiles(el.files)
      el.files = []
    }
    if (el) setData(el)
  }, [el])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const getCategoryList = useCallback(async () => {
    try {
      const result = await salesApi.getCategoryList()
      if (result) setCategoryList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const getSaleItemList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) {
        const filtered = result?.data.filter((item: SaleItemInterface) => item.id !== el?.id) || []
        setSaleItemList(filtered)
      }
    } catch (error) {}
  }, [salesApi, el?.id])

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)

      const { kitItemId, ...dataWithoutKitItemId } = data

      if (data.id) {
        const {
          createdAt,
          updatedAt,
          category,
          attributes,
          prices,
          ratings,
          selected,
          views,
          totalFavorites,
          totalViews,
          averageRating,
          totalRatings,
          stock_stores,
          iiko_id,
          c1_id,
          moysklad_id,
          addons,
          recommendations,
          items,
          ...rest
        } = dataWithoutKitItemId

        const payload: any = {
          ...rest,
          file: dataInit(files),
          attributes: attributes.map(({ id, categoryAttributeId, ...keepAttrs }) => keepAttrs),
          addons: addons,
          recommendations: recommendations,
          kits: items?.map((item) => ({
            childItemId: item.childItemId,
            quantity: item.quantity,
          })),
        }

        await salesApi.updateSaleItem(payload)
      } else {
        const { attributes, addons, recommendations, items, ...rest } = dataWithoutKitItemId

        const payload: any = {
          ...rest,
          file: dataInit(files),
          attributes: attributes.map(({ id, categoryAttributeId, ...keepAttrs }) => keepAttrs),
          addons: addons,
          recommendations: recommendations,
          kits: items?.map((item) => ({
            childItemId: item.childItemId,
            quantity: item.quantity,
          })),
        }

        await salesApi.createSaleItem(payload)
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [salesApi, data, files, dataInit, getList, setModalActive, showCommonLoader, hideCommonLoader])

  const createCategorySelectList = useCallback(() => {
    let arr: OptionItemInterface[] = []
    for (const item of categoryList) {
      if (item.systemType === SystemType.MODIFIERS) continue
      arr.push({
        id: String(item.id),
        label: `${item.name}`,
        name: item.name,
        value: String(item.id),
      })
    }
    return arr
  }, [categoryList])

  const createSaleItemSelectList = useCallback(() => {
    return saleItemList
      .filter((item) => item.categoryId === data.categoryId)
      .map((item) => {
        let attrsString = ''
        if (item.attributes) {
          const attrs = item.attributes
            .filter((attr) => attr.isRequired)
            .map((attr) => {
              if (attr.fieldType === 0 || attr.fieldType === 1 || attr.valueType === 1) {
                let val = String(attr.value)
                if (attr.unit !== undefined && attr.unit !== -1) {
                  val += ` ${unitTypeToShort(attr.unit)}`
                }
                return `${attr.name}: ${val}`
              }
              return null
            })
            .filter(Boolean)
          if (attrs.length > 0) {
            attrsString = ` (${attrs.join(', ')})`
          }
        }
        return {
          id: String(item.id),
          label: item.name + attrsString,
          name: item.name,
          value: String(item.id),
        }
      })
  }, [saleItemList, data.categoryId])

  const createAddonSelectList = useCallback(() => {
    const excludeIds = (data.addons || []).map((item: Relation) => item.relationId)
    return saleItemList
      .filter((item) => item.isAddon && !excludeIds.includes(Number(item.id)))
      .map((item) => ({
        id: String(item.id),
        label: '#' + item.id + ' ' + item.name,
        name: item.name,
        value: String(item.id),
      }))
  }, [saleItemList, data.addons])

  const createRecommendationSelectList = useCallback(() => {
    const excludeIds = (data.recommendations || []).map((item: Relation) => item.relationId)
    return saleItemList
      .filter((item) => !excludeIds.includes(Number(item.id)))
      .map((item) => ({
        id: String(item.id),
        label: '#' + item.id + ' ' + item.name,
        name: item.name,
        value: String(item.id),
      }))
  }, [saleItemList, data.recommendations])

  const createKitSelectList = useCallback(() => {
    const excludeIds = (data.items || []).map((item) => item.childItemId)
    return saleItemList
      .filter(
        (item) =>
          !item.isAddon &&
          !excludeIds.includes(Number(item.id)) &&
          item.id !== data.id &&
          item.isActive,
      )
      .map((item) => ({
        id: String(item.id),
        label: '#' + item.id + ' ' + item.name,
        name: item.name,
        value: String(item.id),
      }))
  }, [saleItemList, data.items, data.id])

  const createSaleItemSelectListFull = useCallback(
    (field?: 'addons' | 'recommendations' | 'kits') => {
      if (field === 'addons') return createAddonSelectList()
      if (field === 'recommendations') return createRecommendationSelectList()
      if (field === 'kits') return createKitSelectList()
      return []
    },
    [createAddonSelectList, createRecommendationSelectList, createKitSelectList],
  )

  const toggleAction = useCallback(
    (fieldName: string) => {
      setData((prev) => {
        if (fieldName === 'isAddon' && prev.isAddon) {
          return {
            ...prev,
            isAddon: false,
            categoryId: undefined,
            addons: [],
            items: [],
          }
        }
        return { ...prev, [fieldName]: !prev[fieldName] }
      })
    },
    [setData],
  )

  const selectAttrs = useCallback(
    (id?: number) => categoryList.find((c) => c.id === id)?.attributes ?? [],
    [categoryList],
  )

  const resetField = useCallback(
    (fieldName: string) => {
      setData((prev) => ({ ...prev, [fieldName]: '' }))
    },
    [setData],
  )

  const getSaleItem = useCallback(
    (id: number) => saleItemList.find((el) => el.id === id),
    [saleItemList],
  )

  const getItemDisplayText = useCallback((item: SaleItemInterface): string => {
    if (!item.attributes || item.attributes.length === 0) return item.name
    const attrs = item.attributes
      .filter((attr) => attr.isRequired)
      .map((attr) => {
        if (attr.fieldType === 0 || attr.fieldType === 1 || attr.valueType === 1) {
          let val = String(attr.value)
          if (attr.unit !== undefined && attr.unit !== -1) {
            val += ` ${unitTypeToShort(attr.unit)}`
          }
          return `${attr.name}: ${val}`
        }
        return null
      })
      .filter(Boolean)
    return attrs.length > 0 ? `${item.name} (${attrs.join(', ')})` : item.name
  }, [])

  const handleAddRelation = useCallback(
    (field: 'addons' | 'recommendations', relationId: number) => {
      if (relationId)
        setData((prev) => ({
          ...prev,
          [field]: [...(prev[field] || []), { relationId }],
        }))
    },
    [setData],
  )

  const handleRemoveRelation = useCallback(
    (field: 'addons' | 'recommendations', relationId: number) => {
      setData((prev) => ({
        ...prev,
        [field]: (prev[field] || []).filter(
          (item: RelationPayload) => item.relationId !== relationId,
        ),
      }))
    },
    [setData],
  )

  const handleAddKitItem = useCallback(
    (childItemId: number) => {
      if (childItemId) {
        const currentItems = data.items || []
        if (currentItems.some((item) => item.childItemId === childItemId)) return
        setData((prev) => ({
          ...prev,
          items: [...(prev.items || []), { childItemId, quantity: 1 }],
        }))
      }
    },
    [setData, data.items],
  )

  const handleRemoveKitItem = useCallback(
    (childItemId: number) => {
      setData((prev) => ({
        ...prev,
        items: (prev.items || []).filter((item) => item.childItemId !== childItemId),
      }))
    },
    [setData],
  )

  const handleKitQuantityChange = useCallback(
    (childItemId: number, quantity: number) => {
      setData((prev) => ({
        ...prev,
        items: (prev.items || []).map((item) =>
          item.childItemId === childItemId ? { ...item, quantity: Math.max(0, quantity) } : item,
        ),
      }))
    },
    [setData],
  )

  const handleStopToggle = useCallback(
    (storeId: number, isAvailable: boolean) => {
      const isStopped = !isAvailable
      const newitem_stops = [
        ...(data.item_stops || []).filter((s) => s.storeId !== storeId),
        { storeId, isStopped },
      ]
      setData({ ...data, item_stops: newitem_stops })
    },
    [data, setData],
  )

  useEffect(() => {
    if (el) {
      const mappedItems =
        el.items?.map((item: any) => ({
          childItemId: item.childItemId,
          quantity: item.quantity,
        })) || []

      const newData = {
        ...el,
        addons: el.addons?.map((a: { relationId: number }) => ({ relationId: a.relationId })) || [],
        recommendations:
          el.recommendations?.map((r: { relationId: number }) => ({ relationId: r.relationId })) ||
          [],
        items: mappedItems,
        kitItemId: '',
      }
      setData(newData)
    }
  }, [el])

  useEffect(() => {
    getCategoryList()
  }, [getCategoryList])

  useEffect(() => {
    getSaleItemList()
  }, [getSaleItemList])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    getStoreList()
  }, [])

  const isKitValid = useCallback(() => {
    const items = data.items || []
    if (items.length === 0) return true
    return items.every((item) => item.quantity > 0)
  }, [data.items])

  useEffect(() => {
    const kitValid = isKitValid()
    setDisabled(
      Boolean(
        (files.length < minUploadFilesCount && !data.isAddon) ||
        validationResult.find((el) => el.notValid) ||
        !kitValid,
      ),
    )
  }, [files, validationResult, data.isAddon, data.items, isKitValid])

  useEffect(() => {
  if (categoryList.length === 0 || !data.categoryId) return

  const currentCategory = categoryList.find((c) => c.id === Number(data.categoryId))
  const isComplex = currentCategory?.isComplex || false

  setData((prev) => ({
    ...prev,
    attributes: [],
    groupId: isComplex ? null : prev.groupId, 
    items: isComplex ? prev.items : [],
  }))
}, [data.categoryId, categoryList, setData])

  useEffect(() => {
    if (data.isAddon) {
      const systemCat = categoryList.find((el) => el.systemType === SystemType.MODIFIERS)
      setData((prev) => ({
        ...prev,
        categoryId: systemCat?.id?.toString() || '',
        groupId: null,
        addons: [],
        recommendations: [],
        items: [],
        useStock: false,
      }))
    }
  }, [data.isAddon, setData, categoryList])

  const currentCategory = categoryList.find((c) => c.id === Number(data.categoryId))
  const isComplexCategory = currentCategory?.isComplex || false

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    files,
    pairs,
    setFiles,
    setPairs,
    createCategorySelectList,
    disabled,
    toggleAction,
    view,
    setView,
    selectAttrs,
    attrsDisabled,
    setAttrsDisabled,
    createSaleItemSelectList,
    createAddonSelectList,
    createRecommendationSelectList,
    createKitSelectList,
    createSaleItemSelectListFull,
    saleItemList,
    resetField,
    getSaleItem,
    getItemDisplayText,
    handleAddRelation,
    handleRemoveRelation,
    handleAddKitItem,
    handleRemoveKitItem,
    handleKitQuantityChange,
    handleStopToggle,
    storeList,
    isComplexCategory,
  }
}